// positionGate.ts — a small, pure "does this fix make physical sense?" filter.
//
// WHY THIS EXISTS: the tester phones (and any phone-GPS boat) report a raw
// FusedLocation fix once per second. Indoors (a shopping centre) or in a moving
// car with no sky view, Android falls back to Wi-Fi/cell positioning whose true
// error is 50–500 m and which hops between access points every tick. The phone's
// on-device EKF trusts each fix's self-reported accuracy, so those hops sail
// through and the boat "teleports" — and because the map draws a line between
// every consecutive fix, one stationary phone paints a dense zigzag ball.
//
// This gate is the missing server/client-side sanity check: it rejects a fix
// that would require impossible speed (a teleport) or that jumps far outside its
// own reported accuracy radius, while never freezing a boat forever (a resync
// escape hatch re-anchors after a short run of rejects). It is deliberately pure
// and framework-free so it is trivial to reason about and reuse.

export interface GateFix {
    lat: number
    lon: number
    ts: number       // epoch ms
    acc?: number     // reported horizontal accuracy radius, metres (Android's accM)
}

export interface GateConfig {
    /** Implied speed (m/s) above which a step is treated as a GPS teleport. */
    maxSpeedMps: number
    /** Steps shorter than this (m) are always accepted — that's just jitter. */
    minJumpM: number
    /** A jump is "outside its own error" when it exceeds this × the reported accuracy. */
    accMultiple: number
    /** Reported accuracy (m) above which a fix is considered multipath-suspect. */
    accSuspectM: number
    /** After this many consecutive rejects, force-accept (re-anchor) to avoid freezing. */
    resyncStreak: number
    /** ...or force-accept once this long (ms) has passed with no accepted fix. */
    resyncMs: number
}

// Tuned for a mixed boat/car fleet: teleports in a mall are 100s of m/s, so 45
// m/s (162 km/h) cleanly separates "impossible" from a fast highway drive, and a
// sailing boat never gets near it.
export const DEFAULT_GATE: GateConfig = {
    maxSpeedMps: 45,
    minJumpM: 4,
    accMultiple: 1.5,
    accSuspectM: 40,
    resyncStreak: 5,
    resyncMs: 12000,
}

export type GateReason =
    | 'first'            // first fix for this boat — nothing to compare against
    | 'ok'               // plausible move
    | 'jitter'           // sub-minJump wobble, accepted as-is
    | 'resync'           // force-accepted after a reject run (re-anchor)
    | 'teleport'         // rejected: implied speed impossible
    | 'lowAccuracyJump'  // rejected: jump far outside a poor accuracy radius

export interface GateState {
    last: GateFix | null   // last ACCEPTED fix (rejected fixes never advance this)
    rejectStreak: number
    accepted: number
    rejected: number
}

export interface GateOutcome {
    accept: boolean
    reason: GateReason
    distM: number
    dtS: number
    speedMps: number
}

export function newGateState(): GateState {
    return { last: null, rejectStreak: 0, accepted: 0, rejected: 0 }
}

/** Great-circle distance in metres between two lat/lon points. */
export function haversineM(aLat: number, aLon: number, bLat: number, bLon: number): number {
    const R = 6371008.8
    const toRad = Math.PI / 180
    const dLat = (bLat - aLat) * toRad
    const dLon = (bLon - aLon) * toRad
    const la1 = aLat * toRad
    const la2 = bLat * toRad
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

function accept(state: GateState, fix: GateFix, reason: GateReason, distM: number, dtS: number, speedMps: number): { state: GateState; outcome: GateOutcome } {
    return {
        state: { last: fix, rejectStreak: 0, accepted: state.accepted + 1, rejected: state.rejected },
        outcome: { accept: true, reason, distM, dtS, speedMps },
    }
}

function reject(state: GateState, reason: GateReason, distM: number, dtS: number, speedMps: number): { state: GateState; outcome: GateOutcome } {
    return {
        state: { ...state, rejectStreak: state.rejectStreak + 1, rejected: state.rejected + 1 },
        outcome: { accept: false, reason, distM, dtS, speedMps },
    }
}

/**
 * Decide whether `fix` is a physically plausible continuation of the boat's
 * track. Pure: returns the next state (store it) and an outcome (act on it).
 * The caller keeps the boat's marker/trail at `state.last` on a reject.
 */
export function gateFix(state: GateState, fix: GateFix, cfg: GateConfig = DEFAULT_GATE): { state: GateState; outcome: GateOutcome } {
    const prev = state.last
    if (!prev || !Number.isFinite(prev.lat) || !Number.isFinite(prev.lon)) {
        return accept(state, fix, 'first', 0, 0, 0)
    }
    const dtS = Math.max(0.001, (fix.ts - prev.ts) / 1000)
    const distM = haversineM(prev.lat, prev.lon, fix.lat, fix.lon)
    const speedMps = distM / dtS

    // Tiny wobble — never fight it; that just makes the marker feel dead.
    if (distM < cfg.minJumpM) return accept(state, fix, 'jitter', distM, dtS, speedMps)

    const teleport = speedMps > cfg.maxSpeedMps
    const accRef = Math.max(fix.acc ?? 0, prev.acc ?? 0)
    // No speed floor here on purpose: a jump already outside 1.5x its own reported
    // accuracy is implausible regardless of how slowly it happened. A prior version
    // required speedMps > 11.25 m/s too, which was tuned for fast indoor AP-hop
    // teleports — but real low-quality GPS near shore/under sails/marina multipath
    // degrades more slowly (tens of metres over 10-20s), so that floor let exactly
    // this "jump under low GPS quality" pattern straight through the gate.
    const lowAccJump = accRef > cfg.accSuspectM && distM > cfg.accMultiple * accRef

    if (teleport || lowAccJump) {
        // Escape hatch: a real relocation (boat carried, GPS was stuck cold, long
        // gap) must not be rejected forever. Re-anchor after a short reject run or
        // a long silence — measured from the last ACCEPTED fix, which is exactly
        // dtS because rejected fixes don't advance `state.last`.
        if (state.rejectStreak + 1 >= cfg.resyncStreak || fix.ts - prev.ts >= cfg.resyncMs) {
            return accept(state, fix, 'resync', distM, dtS, speedMps)
        }
        return reject(state, teleport ? 'teleport' : 'lowAccuracyJump', distM, dtS, speedMps)
    }
    return accept(state, fix, 'ok', distM, dtS, speedMps)
}
